import { Injectable } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';

/**
 * MatPaginatorIntl that appends "(estimated)" to the paginator range label when
 * the table's total row count is an estimate (heavy views where an exact count is
 * infeasible — e.g. cpv_funder_dashboard). Provided per `app-table` instance via
 * the component's `providers`, so every other paginator in the app is unaffected.
 *
 * Toggle with `estimated` + `changes.next()` (TableComponent does this from the
 * `estimatedCount` table setting). Also translates the standard paginator labels
 * (items per page / next / previous / first / last), which Material otherwise
 * renders in English regardless of the app's selected language.
 */
@Injectable()
export class EstimatedPaginatorIntl extends MatPaginatorIntl {
  /** When true, the range label shows "… of N (estimated)". */
  estimated = false;

  constructor(private translate: TranslateService) {
    super();
    this.translateLabels();
    this.translate.onLangChange.subscribe(() => {
      this.translateLabels();
      this.changes.next();
    });
  }

  private translateLabels(): void {
    this.itemsPerPageLabel = this.translate.instant('PaginatorItemsPerPage');
    this.nextPageLabel = this.translate.instant('PaginatorNextPage');
    this.previousPageLabel = this.translate.instant('PaginatorPreviousPage');
    this.firstPageLabel = this.translate.instant('PaginatorFirstPage');
    this.lastPageLabel = this.translate.instant('PaginatorLastPage');
  }

  override getRangeLabel = (
    page: number,
    pageSize: number,
    length: number,
  ): string => {
    const ofLabel = this.translate.instant('PaginatorOf');
    if (length === 0 || pageSize === 0) {
      return `0 ${ofLabel} ${length}`;
    }
    const len = Math.max(length, 0);
    const startIndex = page * pageSize;
    const endIndex =
      startIndex < len
        ? Math.min(startIndex + pageSize, len)
        : startIndex + pageSize;
    const base = `${startIndex + 1} – ${endIndex} ${ofLabel} ${len}`;
    return this.estimated
      ? `${base} ${this.translate.instant('PaginatorEstimatedSuffix')}`
      : base;
  };
}
